import { ErrorHandler, NgModule } from '@angular/core';
import { APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  DEFAULT_LANGUAGE,
  initializeTranslations,
} from './translations.initializer';
import { AppComponent } from './app.component';
import {
  HttpClient,
  HttpBackend,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { AppRoutingModule } from './app-routing.module';
import { SharedUiModule } from '@firebird-web/shared-ui';
import { META_REDUCERS, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { RouteStoreModule } from '@firebird-web/route-store';
import { environment } from '@firebird-web/shared-config';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { CustomRouteSerializer } from './serializers/custom-route-serializer';
import { TitleCasePipe } from '@angular/common';
import { UtcDatePipe } from '@firebird-web/shared/pipes';
import { AuthorizationInterceptor } from '@firebird-web/acl';
import { RetryInterceptor } from 'libs/shared/services/src/lib/http-interceptors/retry.interceptor';
import { ChunkLoadErrorHandler } from '@firebird-web/shared-services';
import { CreateListService } from '../../../../libs/settings/src/lib/components/create-list/services';
import { AlertsNotificationsModule } from '@firebird-web/alerts-notifications';
import { OAuthModule } from 'angular-oauth2-oidc';
import { AuthService } from 'libs/acl/src/lib/auth.service';
import { PermissionsService } from 'libs/acl/src/lib/permissions.service';
import { UserSettingApiService } from '../../../../libs/user-settings-store/src/lib';
import {
  getMetaReducers,
  getSettingsMetaReducers,
} from '../../../../libs/user-settings-store/src/lib/constants';
import { GridstackModule } from 'gridstack/dist/angular';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import * as Sentry from '@sentry/angular';
import { UserSettingsStoreModule } from '@firebird-web/user-settings-store';
import {
  LocationPickerApiService,
  getLocationPickerMetaReducers,
} from '@firebird-web/location-picker-store';
import { RunDatesService } from '@firebird-web/run-dates-store';
import { ZeusApiActivityInterceptor } from 'libs/ask-zeus/src/lib/services/zeus-api-activity.service';
import { SortablejsModule } from '@dustfoundation/ngx-sortablejs';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

declare let hs: any;

// Skip the Auth intercept request for translation files by using HttpBackend as handler
export function createTranslateLoader(http: HttpBackend): TranslateLoader {
  return new TranslateHttpLoader(
    new HttpClient(http),
    '../assets/i18n/',
    '.json'
  );
}

// Factory function to orchestrate initialization
export function initializeApp(
  authService: AuthService,
  permissionsService: PermissionsService
) {
  return (): Promise<any> => {
    hs.graphicsDir = '/assets/highslide/graphics/';
    hs.showCredits = false;
    // First initialize authService
    return authService.initialize().then(() => {
      // After authService is initialized, initialize permissionsService
      return permissionsService.initialize();
    });
    // You can continue chaining more service initializations here if necessary
  };
}

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    SharedUiModule,
    HttpClientModule,
    RouteStoreModule,
    AlertsNotificationsModule,
    TranslateModule.forRoot({
      defaultLanguage: DEFAULT_LANGUAGE,
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpBackend],
      },
    }),
    StoreModule.forRoot(
      {},
      {
        metaReducers: !environment.production ? [] : [],
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      }
    ),
    EffectsModule.forRoot([]),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: !environment.production,
      connectInZone: true,
    }),
    StoreRouterConnectingModule.forRoot({
      serializer: CustomRouteSerializer,
    }),
    OAuthModule.forRoot(),
    AppRoutingModule,
    GridstackModule,
    SortablejsModule.forRoot({}),
    UserSettingsStoreModule,
  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      // subscriptSizing: 'dynamic' -- MDC otherwise reserves a fixed-height
      // hint/error area under every field even when none is ever shown,
      // which legacy Material never did; this keeps fields collapsed to
      // their visible content, matching the old layout.
      useValue: { appearance: 'fill', subscriptSizing: 'dynamic' },
    },
    {
      provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
      // Material 17 started rendering a mat-pseudo-checkbox selection indicator
      // inside the selected toggle. Every toggle group in this app already
      // signals selection by fill color, so the checkmark is redundant and
      // shifts the label. Legacy Material never drew one.
      useValue: {
        hideSingleSelectionIndicator: true,
        hideMultipleSelectionIndicator: true,
      },
    },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      // top-layer popovers cover body-level Highcharts tooltips (outside: true)
      useValue: { usePopover: false },
    },
    provideLottieOptions({
      player: () => player,
    }),
    TitleCasePipe,
    UtcDatePipe,
    CreateListService,
    RunDatesService,
    {
      provide: ErrorHandler,
      useClass: ChunkLoadErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      multi: true,
      deps: [AuthService, PermissionsService, Sentry.TraceService], // Dependencies to inject into the factory function
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslations,
      multi: true,
      deps: [TranslateService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthorizationInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RetryInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ZeusApiActivityInterceptor,
      multi: true,
    },
    {
      provide: META_REDUCERS,
      deps: [UserSettingApiService],
      useFactory: getMetaReducers,
      multi: true,
    },
    {
      provide: META_REDUCERS,
      deps: [UserSettingApiService],
      useFactory: getSettingsMetaReducers,
      multi: true,
    },
    {
      provide: META_REDUCERS,
      deps: [LocationPickerApiService],
      useFactory: getLocationPickerMetaReducers,
      multi: true,
    },
  ],
  declarations: [AppComponent],
  bootstrap: [AppComponent],
})
export class AppModule {}
